import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import {
  ApiResponse,
  PaginatedPayload,
  PaginationMeta,
} from "types/ApiResponse";
import { addTeamMember, editTeamMember, getTeamMembers } from "./thunk";
import { TeamMember } from "types/team.api";

type RequestStatus = "idle" | "loading" | "succeeded" | "failed";

interface TeamMemberStatus {
  fetch: RequestStatus;
  add: RequestStatus;
  edit: RequestStatus;
  delete: RequestStatus;
}

interface TeamMemberErrors {
  fetch: string | null;
  add: string | null;
  edit: string | null;
  delete: string | null;
}

export interface TeamMemberState {
  teamMembers: TeamMember[];
  ids: number[];
  meta: PaginationMeta;
  status: TeamMemberStatus;
  errors: TeamMemberErrors;
  isLoading: boolean;
  isEditLoading: boolean;
}

export const initialState: TeamMemberState = {
  teamMembers: [],
  ids: [],
  isLoading: false,
  isEditLoading: false,
  meta: {
    current_page: 0,
    from: 0,
    last_page: 0,
    links: [],
    path: "",
    per_page: 0,
    to: 0,
    total: 0,
  },
  status: {
    fetch: "idle",
    add: "idle",
    edit: "idle",
    delete: "idle",
  },
  errors: {
    fetch: null,
    add: null,
    edit: null,
    delete: null,
  },
};

const teamMembersSlice = createSlice({
  name: "teamMember",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    // get Categories
    builder.addCase(getTeamMembers.pending, (state) => {
      state.status.fetch = "loading";
      state.errors.fetch = null;
      state.isLoading = true;
    });
    builder.addCase(
      getTeamMembers.fulfilled,
      (
        state,
        action: PayloadAction<ApiResponse<PaginatedPayload<TeamMember>>>
      ) => {
        console.log("PIA", action);
        state.status.fetch = "succeeded";
        state.teamMembers = action.payload.payload.data;
        state.ids = action.payload.payload.data.map(
          (teamMember) => teamMember.id
        );
        state.meta = action?.payload?.payload?.meta;
        state.isLoading = false;
      }
    );
    builder.addCase(getTeamMembers.rejected, (state, action) => {
      if (state.status) {
        state.status.fetch = "failed";
      }
      if (state.errors) {
        state.errors.fetch = action.payload || null;
      }
      state.isLoading = false;
    });

    builder.addCase(addTeamMember.pending, (state) => {
      state.status.add = "loading";
      state.errors.add = null;
    });
    builder.addCase(addTeamMember.fulfilled, (state, action) => {
      state.status.add = "succeeded";
      const addedTeamMember = action.payload.payload;
      state.teamMembers.unshift(addedTeamMember);
      state.ids.unshift(addedTeamMember.id);
      if (state.teamMembers.length > state.meta.per_page) {
        state.meta.to--;
        state.teamMembers.pop();
      }
      state.meta.to++;
      state.meta.total++;
      state.meta.last_page = Math.ceil(state.meta.total / state.meta.per_page);
    });
    builder.addCase(addTeamMember.rejected, (state, action) => {
      state.status.add = "failed";
      state.errors.add = action.payload || null;
    });

    builder.addCase(editTeamMember.pending, (state) => {
      state.status.edit = "loading";
      state.errors.edit = null;
      state.isEditLoading = true;
    });
    builder.addCase(editTeamMember.fulfilled, (state, action) => {
      state.status.edit = "succeeded";
      const updatedTeamMember = action.payload.payload;
      const index = state.teamMembers.findIndex(
        (teamMember) => teamMember.id === updatedTeamMember.id
      );
      console.log(updatedTeamMember);
      state.teamMembers[index] = updatedTeamMember;
      state.isEditLoading = false;
    });
    builder.addCase(editTeamMember.rejected, (state, action) => {
      state.status.edit = "failed";
      state.errors.edit = action.payload || null;
      state.isEditLoading = false;
    });

    // // // delete Category
    // builder.addCase(deleteSection.pending, (state) => {
    //   state.status.delete = "loading";
    //   state.errors.delete = null;
    // });
    // builder.addCase(
    //   deleteSection.fulfilled,
    //   (state, action: PayloadAction<ApiResponse<number>>) => {
    //     state.status.delete = "succeeded";
    //     const deletedSectionId = action.payload.payload;
    //     state.sections = state.sections.filter(
    //       (item) => item.id !== deletedSectionId
    //     );
    //     state.ids = state.ids.filter((id) => id !== deletedSectionId);
    //   }
    // );
    // builder.addCase(deleteSection.rejected, (state, action) => {
    //   state.status.delete = "failed";
    //   state.errors.delete = action.payload || null;
    // });
  },
});

export default teamMembersSlice.reducer;

export const selectTeamMembersState = (state: any) =>
  state.teamMember as typeof initialState;
